/**
 * Compute-on-read values of calculated KPIs (plan §3.1, §6). No weekly value is ever stored for a calculated KPI;
 * given the KPI definitions and the *manual* values (stored, or staged in the browser), `buildValueResolver`
 * answers any KPI's value for any week, evaluating formulas lazily in dependency order with memoisation. The
 * Tracker, Reports and the Board read every number through it, so they cannot disagree.
 *
 * - A **restricted** or **inactive** KPI resolves blank, and so does every formula that uses it.
 * - A calculated KPI resolves **only** through its formula: rows it kept from its manual past (dormant history)
 *   are never read. A calculated KPI without a usable formula is blank.
 * - Blank in, blank out — never 0, NaN or Infinity (see `evaluateFormula`). A text value feeds a formula as blank.
 * - A cycle, which the save path rejects, resolves blank through a runtime guard rather than recursing forever.
 */
import { evaluateFormula } from './formula.js';
/**
 * `(kpiId, weekId) → value`. A KPI with no definition is read straight from `rawValueAt` (callers that only pass
 * calculated definitions keep working); a defined one follows the rules above.
 */
export const buildValueResolver = (defs, rawValueAt) => {
    const byId = new Map(defs.map((def) => [def.id, def]));
    const memo = new Map();
    const inFlight = new Set();
    const resolve = (kpiId, weekId) => {
        const def = byId.get(kpiId);
        if (!def) {
            return rawValueAt(kpiId, weekId) ?? null;
        }
        if (def.restricted || !def.active) {
            return null;
        }
        if (def.source === 'MANUAL') {
            return rawValueAt(kpiId, weekId) ?? null;
        }
        const key = `${kpiId}:${weekId}`;
        if (memo.has(key)) {
            return memo.get(key);
        }
        if (inFlight.has(key) || !def.formula) {
            return null;
        }
        inFlight.add(key);
        const value = evaluateFormula(def.formula, (dependencyId) => {
            const resolved = resolve(dependencyId, weekId);
            return typeof resolved === 'number' ? resolved : null;
        });
        inFlight.delete(key);
        memo.set(key, value);
        return value;
    };
    return resolve;
};
/**
 * Every calculated KPI's value for the given weeks, keyed `${kpiId}:${weekId}` like the tracker's value map; a
 * blank is absent, never 0. For server-side payloads (Reports, Board) that ship resolved numbers.
 */
export const resolveCalculatedValues = (defs, rawValueAt, weekIds) => {
    const resolve = buildValueResolver(defs, rawValueAt);
    const values = new Map();
    for (const def of defs) {
        if (def.source !== 'CALCULATED') {
            continue;
        }
        for (const weekId of weekIds) {
            const value = resolve(def.id, weekId);
            if (typeof value === 'number') {
                values.set(`${def.id}:${weekId}`, value);
            }
        }
    }
    return values;
};
//# sourceMappingURL=value-resolver.js.map